import React from 'react';
import { Download, Edit, Calendar, CheckCircle, ShieldCheck } from 'lucide-react';

interface KYCHeaderProps {
  completionPercentage: number;
  lastUpdated?: string;
  onEdit: () => void;
  onDownloadPDF: () => void;
  backendStatus?: string;
}

/**
 * KYC Review Header with completion status and action buttons
 * Story 2.2.9: KYC Review & Navigation
 */
const KYCHeader: React.FC<KYCHeaderProps> = ({
  completionPercentage,
  lastUpdated,
  onEdit,
  onDownloadPDF,
  backendStatus,
}) => {
  const isApproved = backendStatus === 'approved';
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Unknown';
    }
  };

  const getCompletionColor = () => {
    if (isApproved) return 'bg-green-600';
    if (completionPercentage >= 100) return 'bg-green-600';
    if (completionPercentage >= 75) return 'bg-blue-600';
    if (completionPercentage >= 50) return 'bg-yellow-600';
    return 'bg-gray-400';
  };

  const getCompletionText = () => {
    if (isApproved) return 'Approved';
    if (completionPercentage >= 100) return 'Complete';
    if (completionPercentage >= 75) return 'Almost Complete';
    if (completionPercentage >= 50) return 'In Progress';
    return 'Started';
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${isApproved ? 'border-green-200' : 'border-gray-200'} p-6`}>
      {/* Approved badge */}
      {isApproved && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <ShieldCheck className="w-5 h-5 text-green-600" />
          <span className="text-sm font-semibold text-green-800">
            Approved by Compliance Officer
          </span>
        </div>
      )}

      {/* Title and Actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-kielis-navy">KYC Profile Review</h2>
          <p className="text-sm text-gray-600 mt-1">
            Review and verify all client information
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700
                     rounded-md hover:bg-gray-50 transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>Edit KYC</span>
          </button>
          <button
            onClick={onDownloadPDF}
            className="flex items-center gap-2 px-4 py-2 bg-kielis-navy text-white
                     rounded-md hover:bg-opacity-90 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Download PDF</span>
          </button>
        </div>
      </div>

      {/* Completion Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            {isApproved || completionPercentage >= 100 ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <div className={`w-4 h-4 rounded-full ${getCompletionColor()}`} />
            )}
            <span className="font-medium text-gray-900">
              {getCompletionText()}: {Math.min(Math.round(completionPercentage), 100)}%
            </span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>Last updated: {formatDate(lastUpdated)}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${getCompletionColor()}`}
            style={{ width: `${Math.min(completionPercentage, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default KYCHeader;
